/** Items per page configuration */
export const ITEMS_PER_PAGE = 24;

/**
 * Calculates current page number based on starting index and page size
 * @param {number} pageStart - Starting index of first item (0-based)
 * @param {number} pageSize - Number of items per page
 * @returns {number} Current page number (1-based)
 */
export const getPageNumber = (pageStart, pageSize) => {
	return Math.floor(pageStart / pageSize) + 1;
};

/**
 * Calculates starting index for a given page
 * @param {number} pageNumber - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {number} Starting index (0-based)
 */
export const getPageStart = (pageNumber, pageSize) => {
	return (pageNumber - 1) * pageSize;
};

/**
 * Gets pagination metadata from current state
 * @param {number} totalCount - Total number of items
 * @param {number} pageStart - Current starting index (0-based)
 * @param {number} pageSize - Items per page
 * @returns {Object} Pagination metadata
 */
export const getPaginationMeta = (totalCount, pageStart, pageSize) => {
	const currentPage = getPageNumber(pageStart, pageSize);
	const totalPages = Math.ceil(totalCount / pageSize);
	const endIndex = Math.min(pageStart + pageSize, totalCount);

	return {
		totalCount,
		pageSize,
		pageStart,
		currentPage,
		totalPages,
		endIndex,
		hasNextPage: currentPage < totalPages,
		hasPreviousPage: currentPage > 1,
		isFirstPage: currentPage === 1,
		isLastPage: currentPage === totalPages,
		itemsOnCurrentPage: Math.max(0, endIndex - pageStart)
	};
};

/**
 * Gets items per page (fixed at 24 )
 * @returns {number} Items per page
 */
export const getItemsPerPage = () => {
	return ITEMS_PER_PAGE;
};

/**
 * Return true if paging is needed.
 * Paging isn't needed if there is no data (totalCount === 0)
 * or if the total count is less than the page size
 * @param {Object} paging - Paging object with totalCount and pageSize
 * @returns {boolean}
 */
export const isPagingNeeded = (paging) => {
	return (
		paging && paging.totalCount !== 0 && paging.totalCount > paging.pageSize
	);
};

/**
 * Slices data array for current page based on pageStart and pageSize
 * @param {Array} data - Full dataset
 * @param {number} pageStart - Starting index (0-based)
 * @param {number} pageSize - Items per page
 * @returns {Array} Paginated data slice
 */
export const paginateData = (data, pageStart, pageSize) => {
	if (!Array.isArray(data)) {
		return [];
	}

	const endIndex = Math.min(pageStart + pageSize, data.length);
	return data.slice(pageStart, endIndex);
};
